// libraries.
import dagre from "dagre";
import { Node, Edge, Position } from "@xyflow/react";

// interfaces.
import { ExceptionRule, IncentiveStep } from "./rulesData";

const NODE_WIDTH = 200;
const NODE_HEIGHT = 60;
const EXCEPTIONS_GROUP_HEIGHT = 120;

const buildNodes = (
  steps: IncentiveStep[],
  exceptions: ExceptionRule[],
): Node[] => {
  const nodes: Node[] = [
    {
      id: "start",
      type: "startNode",
      data: { label: "Inmueble en Inventario Full" },
      position: { x: 0, y: 0 },
    },
    {
      id: "decision",
      type: "decisionNode",
      data: { label: "¿Tiene alguna excepción?" },
      position: { x: 0, y: 0 },
    },
  ];

  nodes.push({
    id: "exceptions-group",
    type: "exceptionsGroupNode",
    data: {
      label: "Excepciones",
      exceptions: exceptions.map((exc) => ({ name: exc.name })),
    },
    position: { x: 0, y: 0 },
  });

  nodes.push({
    id: "no-route",
    type: "endNode",
    data: { label: "No aplica ruta de incentivos" },
    position: { x: 0, y: 0 },
  });

  nodes.push({
    id: "route-start",
    type: "routeNode",
    data: { label: "Ruta Default de Incentivos" },
    position: { x: 0, y: 0 },
  });

  steps.forEach((step) => {
    nodes.push({
      id: `step-${step.id}`,
      type: "stepNode",
      data: {
        label: step.name,
        description: step.description,
        adjustment: step.adjustment,
        dayRange: step.dayRange,
        category: step.category,
      },
      position: { x: 0, y: 0 },
    });
  });

  return nodes;
};

const buildEdges = (steps: IncentiveStep[]): Edge[] => {
  const edges: Edge[] = [
    {
      id: "e-start-decision",
      source: "start",
      target: "decision",
      animated: true,
      type: "smoothstep",
    },
  ];

  edges.push({
    id: "e-decision-exceptions",
    source: "decision",
    target: "exceptions-group",
    label: "Sí",
    type: "smoothstep",
  });

  edges.push({
    id: "e-exceptions-noroute",
    source: "exceptions-group",
    target: "no-route",
    type: "smoothstep",
  });

  edges.push({
    id: "e-decision-route",
    source: "decision",
    target: "route-start",
    label: "No",
    type: "smoothstep",
  });

  // All steps connect directly from route-start → same dagre rank (horizontal)
  steps.forEach((step) => {
    edges.push({
      id: `e-step-${step.id}`,
      source: "route-start",
      target: `step-${step.id}`,
      type: "smoothstep",
    });
  });

  return edges;
};

const applyDagreLayout = (
  nodes: Node[],
  edges: Edge[],
  direction: "TB" | "LR" = "TB",
): { nodes: Node[]; edges: Edge[] } => {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: direction, nodesep: 30, ranksep: 70 });

  nodes.forEach((node) => {
    const height =
      node.id === "exceptions-group" ? EXCEPTIONS_GROUP_HEIGHT : NODE_HEIGHT;
    graph.setNode(node.id, { width: NODE_WIDTH, height });
  });

  edges.forEach((edge) => {
    graph.setEdge(edge.source, edge.target);
  });

  dagre.layout(graph);

  const isHorizontal = direction === "LR";

  const layoutedNodes = nodes.map((node) => {
    const pos = graph.node(node.id);
    const height =
      node.id === "exceptions-group" ? EXCEPTIONS_GROUP_HEIGHT : NODE_HEIGHT;
    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: {
        x: pos.x - NODE_WIDTH / 2,
        y: pos.y - height / 2,
      },
    };
  });

  // Align start node center with decision diamond center (110px wide)
  const DECISION_WIDTH = 110;
  const startNode = layoutedNodes.find((n) => n.id === "start");
  const decisionNode = layoutedNodes.find((n) => n.id === "decision");
  if (startNode && decisionNode) {
    const decisionCenterX = decisionNode.position.x + DECISION_WIDTH / 2;
    startNode.position.x = decisionCenterX - NODE_WIDTH / 2;
  }

  return { nodes: layoutedNodes, edges };
};

export const getSummaryTree = (
  steps: IncentiveStep[],
  exceptions: ExceptionRule[],
): { nodes: Node[]; edges: Edge[] } => {
  const nodes = buildNodes(steps, exceptions);
  const edges = buildEdges(steps);
  return applyDagreLayout(nodes, edges);
};
